import { printHousekeepingCharge, printRoomStatusSheet } from '../utils/print';
import { KpiCard, AreaChart, BarChartV, DonutRing, RankBars, DashCard, ActivityFeed, Gauge } from '../components/DashboardWidgets';
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, Field, inputStyle, btnPrimary, btnSm, Empty, ProgressBar , useBreakpoint } from '../components/UI';
import * as api from '../api/client';
import { useAuth } from '../context/AuthContext';

const HK_COLORS = {
  clean:          '#2ecc71',
  dirty:          '#e74c3c',
  inspecting:     '#f39c12',
  'out-of-order': '#c0392b',
};

const LOG_STATUS = {
  'in-progress': { label: 'In Progress',       color: '#3498db', icon: '🔄' },
  'done':        { label: 'Pending Inspection', color: '#f39c12', icon: '⏳' },
  'approved':    { label: 'Approved Clean',     color: '#2ecc71', icon: '✅' },
  'issue':       { label: 'Issue Reported',     color: '#e74c3c', icon: '⚠️' },
};

function fmtTime(dt) {
  return dt ? new Date(dt).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' }) : '—';
}

function tabBtn(active) {
  return {
    padding: '7px 16px', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
    fontSize: 12, fontWeight: 600, border: '1px solid',
    background: active ? 'var(--gold)' : 'var(--bgInput)',
    borderColor: active ? 'var(--gold)' : 'var(--borderMid)',
    color: active ? 'var(--green-dark)' : '#666',
  };
}

function Chip({ label, color }) {
  const c = color || 'var(--textMuted)';
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: c, background: c + '22', padding: '3px 10px', borderRadius: 10, textTransform: 'capitalize' }}>
      {label}
    </span>
  );
}

function InfoRow({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
      <span style={{ color:'var(--textFaint)' }}>{label}</span>
      <span style={{ color: color || 'var(--textMuted)', fontWeight: 600, textTransform: 'capitalize' }}>{value}</span>
    </div>
  );
}

export default function HousekeepingPage({ notify }) {
  const bp = useBreakpoint();
  const { user } = useAuth();

  const [rooms,      setRooms]      = useState([]);
  const [logs,       setLogs]       = useState([]);
  const [activeLogs, setActiveLogs] = useState([]);
  const [hkStaff,    setHkStaff]   = useState([]);

  const [view,         setView]         = useState('board');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedLog,  setSelectedLog]  = useState(null);

  const [filterFloor,  setFilterFloor]  = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQ,      setSearchQ]      = useState('');
  const [assignStaff,  setAssignStaff]  = useState('');

  const [issueModal,   setIssueModal]   = useState(false);
  const [issueText,    setIssueText]    = useState('');
  const [restoreModal, setRestoreModal] = useState(null);
  const [restoreText,  setRestoreText]  = useState('');

  const isAdmin = ['admin', 'reception'].includes(user?.role);

  const load = useCallback(async () => {
    try {
      const [r, l, a, u] = await Promise.all([
        api.getRooms(),
        api.getHousekeepingLogs(),
        api.getHousekeepingLogs('?active=true'),
        api.getUsers(),
      ]);
      const roomList = r.data.results || r.data;
      const logList  = l.data.results || l.data;
      const actList  = a.data.results || a.data;
      setRooms(roomList);
      setLogs(logList);
      setActiveLogs(actList);
      setHkStaff((u.data.results || u.data).filter(x => ['housekeeping','admin'].includes(x.role)));
      if (selectedLog) {
        const fresh = logList.find(x => x.id === selectedLog.id);
        if (fresh) setSelectedLog(fresh);
      }
      if (selectedRoom) {
        const fresh = roomList.find(x => x.id === selectedRoom.id);
        if (fresh) setSelectedRoom(fresh);
      }
    } catch {}
  }, []); // eslint-disable-line

  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);

  const today        = new Date().toISOString().slice(0, 10);
  const statDirty    = rooms.filter(r => r.hk_status === 'dirty').length;
  const statProgress = activeLogs.filter(l => l.status === 'in-progress').length;
  const statPending  = logs.filter(l => l.status === 'done').length;
  const statClean    = rooms.filter(r => r.hk_status === 'clean').length;
  const statOOO      = rooms.filter(r => r.hk_status === 'out-of-order').length;
  const statDoneToday= logs.filter(l => l.status === 'approved' && l.approved_at?.startsWith(today)).length;

  const openDetail = (room) => {
    const log = activeLogs.find(l => l.room === room.id) || null;
    setSelectedRoom(room);
    setSelectedLog(log);
    setView('detail');
  };

  const startCleaning = async (room) => {
    try {
      const payload = { room: room.id };
      if (assignStaff) payload.assigned_to = assignStaff;
      const res = await api.createHousekeepingLog(payload);
      setSelectedLog(res.data);
      setSelectedRoom(room);
      setView('detail');
      await load();
      notify('Cleaning started — Room ' + room.number);
    } catch (e) { notify(e.response?.data?.error || 'Error starting cleaning', 'error'); }
  };

  const toggleItem = async (key, current) => {
    if (!selectedLog) return;
    const updated = {
      ...(selectedLog.checklist_items || []).reduce((a, c) => ({ ...a, [c.key]: c.done }), {}),
      [key]: !current,
    };
    try {
      const res = await api.updateChecklist(selectedLog.id, { checklist: updated });
      setSelectedLog(res.data);
      load();
    } catch { notify('Error updating checklist', 'error'); }
  };

  const submitForInspection = async () => {
    if (!selectedLog) return;
    try {
      const res = await api.markCleaningDone(selectedLog.id);
      setSelectedLog(res.data);
      load();
      notify('Room submitted for inspection');
    } catch (e) { notify(e.response?.data?.error || 'Error', 'error'); }
  };

  const approveRoom = async (log) => {
    try {
      const res = await api.approveRoom(log.id);
      setSelectedLog(res.data);
      load();
      notify('Room ' + log.room_number + ' approved and marked clean!');
    } catch (e) { notify(e.response?.data?.error || 'Error', 'error'); }
  };

  const submitIssue = async () => {
    if (!issueText.trim()) { notify('Describe the issue', 'error'); return; }
    try {
      await api.reportHkIssue(selectedLog.id, { issue: issueText });
      setIssueModal(false); setIssueText('');
      setView('board'); setSelectedRoom(null); setSelectedLog(null);
      load();
      notify('Issue reported — room marked Out of Order');
    } catch { notify('Error', 'error'); }
  };

  const submitRestore = async () => {
    if (!restoreModal) return;
    try {
      await api.restoreRoom({ room: restoreModal.id, resolution: restoreText });
      setRestoreModal(null); setRestoreText('');
      load();
      notify('Room ' + restoreModal.number + ' restored — queued for cleaning');
    } catch (e) { notify(e.response?.data?.error || 'Could not restore room', 'error'); }
  };

  const floors = [...new Set(rooms.map(r => r.floor))].sort((a, b) => a - b);

;
  const priority = { dirty: 0, 'out-of-order': 1, inspecting: 2, clean: 3 };
  const visibleRooms = rooms
    .filter(r => {
      if (filterFloor !== 'all' && r.floor !== parseInt(filterFloor)) return false;
      if (filterStatus !== 'all' && r.hk_status !== filterStatus) return false;
      if (searchQ && !r.number.toLowerCase().includes(searchQ.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => (priority[a.hk_status] ?? 4) - (priority[b.hk_status] ?? 4));

  const pendingInspection = logs.filter(l => l.status === 'done');
  const ooRooms           = rooms.filter(r => r.hk_status === 'out-of-order');

  // ── Issue modal ──────────────────────────────────────────────────────────────
  const IssueModal = issueModal ? (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.8)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'var(--bgCard)', border:'1px solid #e74c3c55', borderRadius:8, padding:24, maxWidth:460, width:'100%', overflowX:'hidden' }}>
        <div style={{ fontSize:16, fontWeight:700, color:'#e74c3c', marginBottom:16 }}>
          ⚠️ Report Issue{selectedRoom ? ' — Room ' + selectedRoom.number : ''}
        </div>
        <Field label="Describe the issue *">
          <textarea style={{ ...inputStyle, minHeight:100, resize:'vertical' }}
            value={issueText} onChange={e=>setIssueText(e.target.value)}
            placeholder="e.g. Broken AC, plumbing leak, damaged furniture…" autoFocus />
        </Field>
        <div style={{ fontSize:12, color:'var(--textFaint)', marginBottom:14 }}>This marks the room Out of Order and alerts management.</div>
        <div style={{ display:'flex', gap:8 }}>
          <button style={{ ...btnPrimary, background:'#e74c3c', marginTop:0 }} onClick={submitIssue}>⚠️ Submit Report</button>
          <button style={{ ...btnSm, padding:'11px 20px' }} onClick={()=>{ setIssueModal(false); setIssueText(''); }}>Cancel</button>
        </div>
      </div>
    </div>
  ) : null;

  // ── Restore modal ────────────────────────────────────────────────────────────
  const RestoreModal = restoreModal ? (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.8)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'var(--bgCard)', border:'1px solid var(--gold)55', borderRadius:8, padding:24, maxWidth:460, width:'100%', overflowX:'hidden' }}>
        <div style={{ fontSize:16, fontWeight:700, color:'var(--gold)', marginBottom:6 }}>🔧 Restore Room {restoreModal.number}</div>
        <div style={{ fontSize:12, color:'var(--textFaint)', marginBottom:16 }}>Clears Out of Order and queues the room for cleaning.</div>
        <Field label="Resolution / Work Done">
          <textarea style={{ ...inputStyle, minHeight:80, resize:'vertical' }}
            value={restoreText} onChange={e=>setRestoreText(e.target.value)}
            placeholder="e.g. AC repaired by technician, plumbing fixed…" autoFocus />
        </Field>
        <div style={{ display:'flex', gap:8, marginTop:14 }}>
          <button style={{ ...btnPrimary, marginTop:0 }} onClick={submitRestore}>✅ Restore Room</button>
          <button style={{ ...btnSm, padding:'11px 20px' }} onClick={()=>{ setRestoreModal(null); setRestoreText(''); }}>Cancel</button>
        </div>
      </div>
    </div>
  ) : null;

  // ─────────────────────────────────────────────────────────────────────────────
  // HISTORY VIEW
  // ─────────────────────────────────────────────────────────────────────────────
  if (view === 'history') {
    return (
      <div>
        <PageHeader icon="🧹" title="Housekeeping" sub="Room cleaning, checklists and readiness" />
        <div style={{ display:'flex', gap:8, marginBottom:16 }}>
          <button onClick={()=>setView('board')}   style={tabBtn(false)}>🏨 Room Board</button>
          <button onClick={()=>setView('history')} style={tabBtn(true)}>📋 Log History</button>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <div />
          <button style={{ background:'var(--bgInput)', border:'1px solid var(--border)', borderRadius:6, padding:'6px 14px', color:'var(--textMuted)', fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }} onClick={() => printRoomStatusSheet(rooms)}>
            🖨 Print Room Status Sheet
          </button>
        </div>
        <Card title={'Housekeeping History (' + logs.length + ')'}>
          {logs.length === 0 ? <Empty text="No logs yet" /> : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead><tr style={{ background:'var(--bgInput)' }}>
                  {['Room','Type','Status','Assigned','Progress','Started','Approved By','Time','Print'].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:10, fontWeight:700, color:'var(--textFaint)', borderBottom:'1px solid var(--border)', textTransform:'uppercase', letterSpacing:.5 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {logs.slice(0,60).map((l,i) => (
                    <tr key={l.id} style={{ borderBottom:'1px solid var(--border)', background: i%2 ? 'var(--bgHover)' : 'transparent' }}>
                      <td style={{ padding:'8px 10px', fontWeight:700, color:'var(--textPrimary)' }}>Rm {l.room_number}</td>
                      <td style={{ padding:'8px 10px', color:'var(--textMuted)' }}>{l.room_type}</td>
                      <td style={{ padding:'8px 10px' }}>
                        <span style={{ fontSize:10, fontWeight:700, color:LOG_STATUS[l.status]?.color, background:LOG_STATUS[l.status]?.color+'22', padding:'2px 7px', borderRadius:3 }}>
                          {LOG_STATUS[l.status]?.icon} {LOG_STATUS[l.status]?.label || l.status}
                        </span>
                      </td>
                      <td style={{ padding:'8px 10px', color:'var(--textMuted)' }}>{l.assigned_to_name||'—'}</td>
                      <td style={{ padding:'8px 10px', minWidth:90 }}><ProgressBar pct={l.completion_pct}/><span style={{ fontSize:10, color:'var(--textFaint)' }}>{l.completion_pct}%</span></td>
                      <td style={{ padding:'8px 10px', color:'var(--textFaint)', fontSize:11 }}>{new Date(l.started_at).toLocaleString('en-KE')}</td>
                      <td style={{ padding:'8px 10px', color:'var(--textMuted)' }}>{l.checked_by_name||'—'}</td>
                      <td style={{ padding:'8px 10px', color:l.approved_at?'var(--gold)':'var(--textFaint)', fontSize:11 }}>{fmtTime(l.approved_at)}</td>
                      <td style={{ padding:'6px 6px' }}>
                        <button style={{ fontSize:10, padding:'3px 8px', background:'var(--bgInput)', border:'1px solid var(--border)', borderRadius:3, color:'var(--textMuted)', cursor:'pointer' }}
                          onClick={() => printHousekeepingCharge({ id: l.id, room_number: l.room_number, room_type: l.room_type, type: 'service', logged_by: l.assigned_to_name, date: l.started_at?.slice(0,10), status: l.status, description: `${l.room_type||'Room'} ${l.room_number} — Housekeeping ${l.status} (${l.completion_pct}% complete)`, amount: 0 })}>
                          🖨
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        {IssueModal}{RestoreModal}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // DETAIL VIEW
  // ─────────────────────────────────────────────────────────────────────────────
  if (view === 'detail' && selectedRoom) {
    const log   = selectedLog;
    const isOOO = selectedRoom.hk_status === 'out-of-order';

    return (
      <div>
        <PageHeader icon="🧹" title="Housekeeping" sub="Room cleaning, checklists and readiness" />
        <button style={{ ...btnSm, marginBottom:14, background:'var(--bgInput)' }}
          onClick={()=>{ setView('board'); setSelectedRoom(null); setSelectedLog(null); }}>
          ← Back to Board
        </button>

        <div style={{ display:'grid', gridTemplateColumns: bp === 'sm' ? '1fr' : '1fr 220px', gap:16, alignItems:'start' }}>

          <Card title={'Room ' + selectedRoom.number + ' — ' + selectedRoom.type}>
            <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
              <Chip label={'Floor ' + selectedRoom.floor} color="var(--textMuted)" />
              <Chip label={selectedRoom.status === 'occupied' ? 'Occupied' : 'Vacant'} color={selectedRoom.status==='occupied'?'#e67e22':'var(--gold)'} />
              <Chip label={selectedRoom.hk_status} color={HK_COLORS[selectedRoom.hk_status]||'var(--textMuted)'} />
            </div>

            {/* Out of Order */}
            {isOOO && (
              <div style={{ padding:16, background:'var(--bgDanger)', border:'1px solid var(--danger)55', borderRadius:6, marginBottom:16 }}>
                <div style={{ color:'#e74c3c', fontWeight:700, fontSize:14, marginBottom:6 }}>⛔ Room Out of Order</div>
                {selectedRoom.hk_notes && <div style={{ fontSize:12, color:'var(--textMuted)', marginBottom:10 }}>{selectedRoom.hk_notes}</div>}
                {isAdmin
                  ? <button style={{ ...btnPrimary, background:'#8B0000', marginTop:0, width:'auto' }} onClick={()=>setRestoreModal(selectedRoom)}>🔧 Restore Room</button>
                  : <div style={{ fontSize:12, color:'var(--textFaint)' }}>Contact reception or admin to restore this room.</div>
                }
              </div>
            )}

            {/* No log, not OOO */}
            {!log && !isOOO && (
              <div style={{ textAlign:'center', padding:'30px 0' }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🧹</div>
                <div style={{ color:'var(--textMuted)', marginBottom:16 }}>No active cleaning task.</div>
                {isAdmin && (
                  <select style={{ ...inputStyle, width:'auto', marginRight:8, marginBottom:12 }} value={assignStaff} onChange={e=>setAssignStaff(e.target.value)}>
                    <option value="">Assign to… (optional)</option>
                    {hkStaff.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                )}
                <br />
                <button style={{ ...btnPrimary, width:'auto' }} onClick={()=>startCleaning(selectedRoom)}>🧹 Start Cleaning</button>
              </div>
            )}

            {/* Active log */}
            {log && (
              <>
                <div style={{ marginBottom:14 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                    <span style={{ fontSize:13, color:'var(--textMuted)' }}>{log.completion_pct}% complete</span>
                    <span style={{ fontSize:11, fontWeight:700, color:LOG_STATUS[log.status]?.color, background:LOG_STATUS[log.status]?.color+'22', padding:'2px 8px', borderRadius:3 }}>
                      {LOG_STATUS[log.status]?.icon} {LOG_STATUS[log.status]?.label}
                    </span>
                  </div>
                  <ProgressBar pct={log.completion_pct} />
                  {log.assigned_to_name && <div style={{ fontSize:11, color:'var(--textFaint)', marginTop:4 }}>👤 {log.assigned_to_name}</div>}
                  <div style={{ fontSize:11, color:'var(--textFaint)', marginTop:2 }}>Started: {fmtTime(log.started_at)}</div>
                </div>

                {/* Checklist */}
                <div style={{ border:'1px solid var(--border)', borderRadius:5, overflow:'hidden', marginBottom:14 }}>
                  <div style={{ padding:'8px 14px', background:'var(--bgInput)', fontSize:11, fontWeight:700, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1 }}>
                    Checklist — {(log.checklist_items||[]).filter(i=>i.done).length}/{(log.checklist_items||[]).length} done
                  </div>
                  {(log.checklist_items||[]).map((item, idx) => {
                    const locked = log.status === 'approved' || log.status === 'done' || log.status === 'issue';
                    return (
                      <div key={item.key}
                        style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 14px', borderTop: idx ? '1px solid var(--border)' : 'none', background: item.done ? 'rgba(201,168,76,0.06)' : 'transparent', cursor: locked ? 'default' : 'pointer' }}
                        onClick={()=>{ if(!locked) toggleItem(item.key, item.done); }}
                      >
                        <div style={{ width:20, height:20, borderRadius:4, flexShrink:0, border:'2px solid '+(item.done?'var(--gold)':'#3a3a3a'), background: item.done?'var(--gold)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, color:'#111', fontWeight:800 }}>
                          {item.done ? '✓' : ''}
                        </div>
                        <span style={{ fontSize:13, color: item.done ? 'var(--gold)' : 'var(--textFaint)', flex:1 }}>{item.label}</span>
                      </div>
                    );
                  })}
                </div>

                {log.status === 'in-progress' && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:8 }}>
                    <button style={{ ...btnPrimary, padding:'12px', background: log.completion_pct===100 ? 'var(--gold)' : 'var(--gold)', color:'#111', fontWeight:700, fontSize:14 }}
                      onClick={submitForInspection}>
                      {log.completion_pct===100 ? '✅ Submit for Inspection' : '🏁 Submit (' + log.completion_pct + '% done)'}
                    </button>
                    <button style={{ ...btnSm, padding:'12px 16px', background:'var(--bgDanger)', color:'#e74c3c' }} onClick={()=>setIssueModal(true)}>⚠ Issue</button>
                  </div>
                )}

                {log.status === 'done' && (
                  <div>
                    <div style={{ padding:'10px 14px', background:'#1a1200', border:'1px solid #f39c1244', borderRadius:6, color:'#f39c12', fontSize:13, marginBottom:10, textAlign:'center' }}>
                      ⏳ Submitted — awaiting approval from reception/admin
                    </div>
                    {isAdmin && (
                      <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:8 }}>
                        <button style={{ ...btnPrimary, padding:'12px', background:'var(--gold)', color:'#111', fontWeight:700 }} onClick={()=>approveRoom(log)}>
                          ✅ Approve — Mark Room Clean
                        </button>
                        <button style={{ ...btnSm, padding:'12px 16px', background:'var(--bgDanger)', color:'#e74c3c' }} onClick={()=>setIssueModal(true)}>⚠ Issue</button>
                      </div>
                    )}
                  </div>
                )}

                {log.status === 'approved' && (
                  <div style={{ padding:'14px', background:'var(--bgInput)', border:'1px solid var(--gold)44', borderRadius:6, display:'flex', gap:12, alignItems:'center' }}>
                    <span style={{ fontSize:28 }}>✅</span>
                    <div>
                      <div style={{ color:'var(--gold)', fontWeight:700 }}>Room Approved Clean</div>
                      <div style={{ fontSize:11, color:'var(--textFaint)', marginTop:2 }}>{fmtTime(log.approved_at)} · {log.checked_by_name || 'Supervisor'}</div>
                    </div>
                  </div>
                )}

                {log.status === 'issue' && (
                  <div style={{ padding:'14px', background:'var(--bgDanger)', border:'1px solid var(--danger)44', borderRadius:6 }}>
                    <div style={{ color:'#e74c3c', fontWeight:700, marginBottom:6 }}>⚠️ Issue Reported</div>
                    <div style={{ fontSize:12, color:'var(--textMuted)', marginBottom: isAdmin ? 12 : 0 }}>{log.issue_desc}</div>
                    {isAdmin && <button style={{ ...btnSm, background:'var(--bgDanger)', color:'var(--danger)', border:'1px solid var(--danger)44' }} onClick={()=>setRestoreModal(selectedRoom)}>🔧 Restore This Room</button>}
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Sidebar */}
          <div style={{ display:'grid', gap:10 }}>
            <Card title="Room Info">
              <InfoRow label="Number" value={selectedRoom.number} />
              <InfoRow label="Type"   value={selectedRoom.type} />
              <InfoRow label="Floor"  value={'Floor ' + selectedRoom.floor} />
              <InfoRow label="Status" value={selectedRoom.status} color={selectedRoom.status==='available'?'var(--gold)':'#e67e22'} />
              <InfoRow label="HK"     value={selectedRoom.hk_status} color={HK_COLORS[selectedRoom.hk_status]} />
              {selectedRoom.hk_notes && <div style={{ marginTop:8, fontSize:11, color:'var(--textMuted)', padding:'6px 8px', background:'var(--bgInput)', borderRadius:3 }}>{selectedRoom.hk_notes}</div>}
            </Card>
            {log && (
              <Card title="Task Info">
                <InfoRow label="Progress"  value={log.completion_pct + '%'} color={log.completion_pct===100?'var(--gold)':'var(--gold)'} />
                <InfoRow label="Items"     value={(log.checklist_items||[]).filter(i=>i.done).length + '/' + (log.checklist_items||[]).length} />
                {log.assigned_to_name && <InfoRow label="Assigned" value={log.assigned_to_name} />}
                <InfoRow label="Started"   value={fmtTime(log.started_at)} />
                {log.completed_at && <InfoRow label="Submitted" value={fmtTime(log.completed_at)} />}
                {log.approved_at  && <InfoRow label="Approved"  value={fmtTime(log.approved_at)} color="var(--gold)" />}
              </Card>
            )}
          </div>
        </div>
        {IssueModal}{RestoreModal}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // BOARD VIEW
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader icon="🧹" title="Housekeeping" sub="Room cleaning, checklists and readiness" />

      <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
        <button onClick={()=>setView('board')}   style={tabBtn(true)}>🏨 Room Board</button>
        <button onClick={()=>setView('history')} style={tabBtn(false)}>📋 Log History</button>
        <div style={{ marginLeft:'auto', display:'flex', gap:6, flexWrap:'wrap' }}>
          {statDirty    > 0 && <Chip label={statDirty + ' dirty'}            color="#e74c3c" />}
          {statProgress > 0 && <Chip label={statProgress + ' cleaning'}      color="#3498db" />}
          {statPending  > 0 && <Chip label={statPending + ' awaiting OK'}    color="#f39c12" />}
          {statOOO      > 0 && <Chip label={statOOO + ' out of order'}       color="#c0392b" />}
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))', gap:10, marginBottom:16 }}>
        {[
          { label:'Needs Cleaning', value:statDirty,     color:'#e74c3c', icon:'🧹' },
          { label:'In Progress',    value:statProgress,  color:'#3498db', icon:'🔄' },
          { label:'Awaiting OK',    value:statPending,   color:'#f39c12', icon:'⏳' },
          { label:'Clean & Ready',  value:statClean,     color:'var(--gold)', icon:'✅' },
          { label:'Done Today',     value:statDoneToday, color:'var(--gold)', icon:'📅' },
          { label:'Out of Order',   value:statOOO,       color:'#c0392b', icon:'⛔' },
        ].map(s=>(
          <div key={s.label} style={{ background:'var(--bgCard)', border:'1px solid '+s.color+'22', borderTop:'3px solid '+s.color, borderRadius:6, padding:'10px 12px' }}>
            <div style={{ fontSize:10, color:'var(--textFaint)', marginBottom:3, textTransform:'uppercase', letterSpacing:.5 }}>{s.icon} {s.label}</div>
            <div style={{ fontSize:24, fontWeight:800, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Pending inspection alert */}
      {isAdmin && pendingInspection.length > 0 && (
        <div style={{ background:'#1a1200', border:'1px solid #f39c1255', borderRadius:6, padding:'12px 16px', marginBottom:12 }}>
          <div style={{ fontWeight:700, color:'#f39c12', marginBottom:8 }}>⏳ {pendingInspection.length} room{pendingInspection.length>1?'s':''} awaiting your inspection</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {pendingInspection.map(log=>(
              <button key={log.id} style={{ ...btnSm, background:'#2a2000', color:'#f39c12', border:'1px solid #f39c1244' }}
                onClick={()=>{ const room=rooms.find(r=>r.id===log.room); if(room){setSelectedRoom(room);setSelectedLog(log);setView('detail');} }}>
                Rm {log.room_number} — {log.completion_pct}% · {log.assigned_to_name||'Staff'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Out of order alert */}
      {isAdmin && ooRooms.length > 0 && (
        <div style={{ background:'var(--bgDanger)', border:'1px solid var(--danger)55', borderRadius:6, padding:'12px 16px', marginBottom:12 }}>
          <div style={{ fontWeight:700, color:'#e74c3c', marginBottom:8 }}>⛔ {ooRooms.length} room{ooRooms.length>1?'s':''} out of order</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {ooRooms.map(room=>(
              <button key={room.id} style={{ ...btnSm, background:'var(--bgDanger)', color:'var(--danger)', border:'1px solid var(--danger)44' }}
                onClick={()=>setRestoreModal(room)}>
                🔧 Restore Rm {room.number}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <input style={{ ...inputStyle, width:110, padding:'6px 10px' }} placeholder="Room #…" value={searchQ} onChange={e=>setSearchQ(e.target.value)} />
        <select style={{ ...inputStyle, width:'auto', padding:'6px 10px' }} value={filterFloor} onChange={e=>setFilterFloor(e.target.value)}>
          <option value="all">All Floors</option>
          {floors.map(f=><option key={f} value={f}>Floor {f}</option>)}
        </select>
        {['all','dirty','inspecting','out-of-order','clean'].map(s=>(
          <button key={s} onClick={()=>setFilterStatus(s)} style={{ background:filterStatus===s?'var(--gold)':'var(--bgInput)', border:'1px solid '+(filterStatus===s?(HK_COLORS[s]||'var(--gold)'):'var(--borderMid)'), borderRadius:4, padding:'5px 12px', cursor:'pointer', fontFamily:'inherit', fontSize:11, color:filterStatus===s?(HK_COLORS[s]||'var(--green-dark)'):'#666', textTransform:'capitalize' }}>
            {s==='all'?'🌐 All':s==='clean'?'✓ clean':s==='dirty'?'● dirty':s==='inspecting'?'🔍 inspecting':'⛔ out-of-order'}
          </button>
        ))}
        {isAdmin && (
          <select style={{ ...inputStyle, width:'auto', padding:'6px 10px', marginLeft:'auto' }} value={assignStaff} onChange={e=>setAssignStaff(e.target.value)}>
            <option value="">Default assign</option>
            {hkStaff.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>

      {/* Room grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(158px,1fr))', gap:10 }}>
        {visibleRooms.map(room=>{
          const log   = activeLogs.find(l=>l.room===room.id);
          const col   = HK_COLORS[room.hk_status]||'var(--textMuted)';
          const isOOO = room.hk_status==='out-of-order';
          return (
            <div key={room.id} style={{ background:'var(--bgCard)', border:'1px solid '+col+'33', borderTop:'4px solid '+col, borderRadius:6, padding:12, opacity:isOOO?.75:1 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:5 }}>
                <span style={{ fontSize:20, fontWeight:800, color:'var(--textPrimary)' }}>{room.number}</span>
                <span style={{ fontSize:9, fontWeight:700, color:col, background:col+'22', padding:'2px 6px', borderRadius:3, textTransform:'uppercase' }}>{room.hk_status}</span>
              </div>
              <div style={{ fontSize:11, color:'var(--textFaint)', marginBottom:4 }}>{room.type} · Floor {room.floor}</div>
              <div style={{ fontSize:11, color:room.status==='occupied'?'#e67e22':'var(--gold)', marginBottom:8, fontWeight:600 }}>
                {room.status==='occupied'?'🔴 Occupied':'🟢 Vacant'}
              </div>
              {log && (
                <div style={{ marginBottom:8 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                    <span style={{ fontSize:10, color: log.status==='done'?'#f39c12':'#3498db' }}>{log.status==='done'?'⏳ Awaiting OK':'🔄 Cleaning…'}</span>
                    <span style={{ fontSize:10, color:'var(--textFaint)' }}>{log.completion_pct}%</span>
                  </div>
                  <ProgressBar pct={log.completion_pct} />
                  {log.assigned_to_name && <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:2 }}>👤 {log.assigned_to_name}</div>}
                </div>
              )}
              {isOOO ? (
                isAdmin
                  ? <button style={{ width:'100%', padding:'7px 0', borderRadius:4, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600, background:'#2a0808', color:'#e74c3c' }} onClick={()=>setRestoreModal(room)}>🔧 Restore Room</button>
                  : <div style={{ fontSize:10, color:'var(--textFaint)', textAlign:'center', marginTop:4 }}>⛔ Out of Order</div>
              ) : log ? (
                <button style={{ width:'100%', padding:'7px 0', borderRadius:4, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600, background:'#0a1a2a', color:'#3498db' }} onClick={()=>openDetail(room)}>
                  → {log.status==='done'?'View / Approve':'Continue'}
                </button>
              ) : (
                <button style={{ width:'100%', padding:'7px 0', borderRadius:4, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600, background:room.hk_status==='clean'?'rgba(201,168,76,0.12)':'var(--bgDanger)', color:room.hk_status==='clean'?'var(--gold)':'var(--danger)' }}
                  onClick={()=>room.hk_status==='clean'?openDetail(room):startCleaning(room)}>
                  {room.hk_status==='clean'?'✓ View':'🧹 Start Cleaning'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {visibleRooms.length===0 && <Empty text="No rooms match the current filters" />}
      {IssueModal}{RestoreModal}
    </div>
  );
}
